import {
  ReportImg,
  ReportImgProps,
} from "@/components/report/commons/ReportImg";
import { Divider, Stack, Typography, TypographyProps } from "@mui/material";
import { ReactNode } from "react";

export const RISK_TYPES = ["good", "caution", "alert", "unknown"] as const;
export type RiskType = (typeof RISK_TYPES)[number];

export type ThreeLevelScalesType = 1 | 2 | 3;
export const ThreeLevelScale: Record<string, ThreeLevelScalesType> = {
  GOOD: 3,
  NORMAL: 2,
  BAD: 1,
} as const;

export interface ImpactScoreResultImgProps
  extends Omit<ReportImgProps, "image_name"> {
  type: ThreeLevelScalesType | null | undefined;
}
export function ImpactScoreResultImg({
  type,
  ...props
}: ImpactScoreResultImgProps) {
  const name =
    type == null || type === ThreeLevelScale.GOOD ? "hyphen" : "cross";
  return (
    <ReportImg image_name={`image_report_result_${name}.svg`} {...props} />
  );
}

export interface ReportDailyResultImgProps
  extends Omit<ReportImgProps, "image_name"> {
  type: ThreeLevelScalesType;
}
export function ReportDailyResultImg({
  type,
  ...props
}: ReportDailyResultImgProps) {
  return (
    <ReportImg
      image_name={
        [
          "image_report_result_cross.svg",
          "image_report_result_triangle.svg",
          "image_report_result_circle.svg",
        ][type - 1]
      }
      {...props}
    />
  );
}

export const RISK_TITLE_LIST = ["低リスク", "リスクあり", "未判定"] as const;
export type RiskTitle = (typeof RISK_TITLE_LIST)[number];

export interface RiskProps {
  type: RiskType;
  title: RiskTitle;
  summary: string | null;
  description_lines: string[];
}

export interface NumberTitleProps extends Omit<TypographyProps, "children"> {
  number: number;
  children: string;
}
export function NumberTitle({ number, children, ...props }: NumberTitleProps) {
  return (
    <Stack sx={{ gap: 2 }}>
      <Stack direction="row" sx={{ gap: 1, alignItems: "center" }}>
        <Typography
          fontFamily="Helvetica Neue"
          fontWeight={500}
          fontSize="28px"
          color="primary.main"
          {...props}
        >
          {number}.
        </Typography>
        <Typography fontWeight={500} fontSize="18px">
          {children}
        </Typography>
      </Stack>
      <Divider />
    </Stack>
  );
}

export interface RiskLabelProps {
  type: RiskType;
  title: string;
  summary: string | null;
}
const RiskLabelColors: Record<
  RiskType,
  { backgroundColor: string; color: string }
> = {
  good: {
    backgroundColor: "#EBF2F8",
    color: "primary.main",
  },
  caution: {
    backgroundColor: "#FFFAF1",
    color: "#FF910B",
  },
  alert: {
    backgroundColor: "#FFF0EF",
    color: "#FF3A30",
  },
  unknown: {
    backgroundColor: "#ebebeb",
    color: "#707070",
  },
};
export function RiskLabel({ type, title, summary }: RiskLabelProps) {
  const color = RiskLabelColors[type];
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      sx={{
        p: 4,
        borderRadius: "8px",
        backgroundColor: color.backgroundColor,
      }}
    >
      <Typography
        fontWeight={500}
        fontSize="16px"
        color={color.color}
        textAlign="justify"
      >
        {title}
      </Typography>
      {summary != null && (
        <Typography
          fontWeight={400}
          fontSize="14px"
          color={color.color}
          textAlign="justify"
        >
          {summary}
        </Typography>
      )}
    </Stack>
  );
}

export interface WrappedSectionProps {
  title: string;
  children: ReactNode;
  color: string;
}
export function WrappedSection({
  title,
  children,
  color,
}: WrappedSectionProps) {
  return (
    <Stack sx={{ position: "relative" }}>
      <Typography
        variant="subtitle2"
        color={color}
        sx={{
          position: "absolute",
          backgroundColor: "#FFFFFF",
          px: 1,
          left: "16px",
        }}
      >
        {title}
      </Typography>
      <Stack
        sx={{
          border: `solid 1px ${color}`,
          borderRadius: 2,
          p: 5,
          mt: "0.875rem",
        }}
      >
        {children}
      </Stack>
    </Stack>
  );
}
